/**
 * useWorkspacePersistence — the one place that turns "keep / stop keeping this
 * workspace" into the two writes it actually needs, and refuses to leave the
 * UI claiming a state the disk does not back (fix round 1, I-4).
 *
 * The two writes:
 *  1. the renderer-persisted registry (`aiTerminalSettings.persistentWorkspaces`),
 *     which is what `createWorkspace` consults when the NEXT AI terminal opens;
 *  2. the folder's on-disk `.jam-persistent` marker, which is what startup
 *     cleanup consults — main receives settings only after the renderer boots,
 *     i.e. strictly after the startup wipe has already run, so the marker is
 *     the only live protection a kept folder has.
 *
 * Why this is a hook and not two `.catch()`s at the call sites: the marker IPC
 * *resolves* `{ success: false }` on failure rather than rejecting, so a bare
 * `.catch()` never fires. The first cut had exactly that bug on both surfaces
 * — the registry write had already landed, so the toggle rendered ON for a
 * folder with no marker, i.e. the user was told their work was kept and would
 * have found out otherwise by losing it. Here the registry write is treated as
 * provisional: if the marker write fails, or the registry refused the entry
 * (a folder-name collision, see `mergePersistentWorkspaces`), it is rolled
 * back and the failure is surfaced.
 *
 * Analytics fires only after both writes succeed — per CLAUDE.md, successful
 * side-effects are tracked on success, not on click — on BOTH directions, with
 * `{ enabled }` and nothing else.
 */

import { useCallback } from 'react';
import { toast } from '../../../components/ui/toaster';
import { useAppStore } from '../../../store/appStore';
import { useAnalytics } from '../../../core/analytics/useAnalytics';

export type WorkspacePersistenceRequest = {
  connectionId: string;
  /** Derived folder name — never user-typed. See `persistentWorkspaceFolderName`. */
  folderName: string;
  /** True to start keeping the workspace, false to stop. */
  keep: boolean;
};

export function useWorkspacePersistence() {
  const setAiTerminalSettings = useAppStore((s) => s.setAiTerminalSettings);
  const { track } = useAnalytics();

  const setPersistence = useCallback(
    async ({ connectionId, folderName, keep }: WorkspacePersistenceRequest): Promise<boolean> => {
      const previous = useAppStore.getState().aiTerminalSettings.persistentWorkspaces?.[connectionId];

      setAiTerminalSettings({
        persistentWorkspaces: { [connectionId]: keep ? { folderName } : null },
      });

      const landed = useAppStore.getState().aiTerminalSettings.persistentWorkspaces?.[connectionId];
      // The registry rejects an entry whose folder another connection already
      // claims, so "keep" can legitimately fail to take effect here.
      if (keep && !landed) {
        reportFailure(keep, 'Another connection is already keeping that workspace folder.');
        return false;
      }

      const result = await window.electronAPI?.aiWorkspace
        ?.setWorkspacePersistence?.({ folderName, persistent: keep })
        .catch((error: unknown) => {
          console.error('[useWorkspacePersistence] setWorkspacePersistence threw:', error);
          return { success: false, error: error instanceof Error ? error.message : undefined };
        });

      // An absent bridge (browser mode) is a failure, not a silent success:
      // without the marker the folder has no protection at startup.
      if (!result?.success) {
        restore(setAiTerminalSettings, connectionId, previous);
        reportFailure(keep, result?.error);
        return false;
      }

      track('feature:ai_workspace_persist', { enabled: keep });
      return true;
    },
    [setAiTerminalSettings, track]
  );

  return { setPersistence };
}

function restore(
  setAiTerminalSettings: (update: {
    persistentWorkspaces: Record<string, { folderName: string } | null>;
  }) => void,
  connectionId: string,
  previous: { folderName: string } | undefined
): void {
  setAiTerminalSettings({ persistentWorkspaces: { [connectionId]: previous ?? null } });
}

function reportFailure(keep: boolean, error?: string): void {
  toast.error({
    title: keep ? "Couldn't keep this workspace" : "Couldn't stop keeping this workspace",
    description: keep
      ? `The workspace folder could not be marked as kept, so it would still be deleted. ${error ?? ''}`.trim()
      : `The workspace folder is still marked as kept. ${error ?? ''}`.trim(),
    testId: 'ai-workspace-persist-error',
  });
}
